import { skipToken, useSuspenseQuery } from "@apollo/client";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import DonutChart from "components/Dashboard/DonutChart";
import { ProcessedManifestCorrectionStatsQuery } from "generated-graphql/graphql";
import { useQueryParams } from "hooks/useQueryParams";
import { useTenant } from "hooks/useTenant";
import { Suspense } from "react";

import { PROCESSED_MANIFEST_CORRECTION_STATS_QUERY } from "./api";
import {
  buildComplianceSegments,
  COMPLIANCE_COLORS,
  complianceHeadline,
  correctionComplianceEmptyMessage,
  isCorrectionStatsEmpty,
} from "./correctionCompliance";
import { upsertOmnisearchTerm } from "./omnisearch";

// Re-exported for callers that historically imported the palette from this
// tile (e.g. VendorBreakdownTile); the source of truth now lives alongside the
// rest of the compliance logic in ./correctionCompliance.
export { COMPLIANCE_COLORS };

interface Props {
  shippedYear: number;
  skip?: boolean;
  selectedVendor?: string | null;
}

export function CorrectionComplianceTile({ shippedYear, skip = false }: Props) {
  return (
    <DashboardTile size={{ xs: 12, md: 6, lg: 4 }}>
      <DashboardTile.Header title="30-Day Correction Compliance" />
      <DashboardTile.ContentArea>
        <Suspense
          fallback={
            <Stack
              spacing={1}
              data-testid="tile-skeleton"
              alignItems="center"
              justifyContent="center"
              sx={{ minHeight: "18rem" }}
            >
              <Skeleton variant="circular" width={240} height={240} />
            </Stack>
          }
        >
          <CorrectionComplianceSummary shippedYear={shippedYear} skip={skip} />
        </Suspense>
      </DashboardTile.ContentArea>
    </DashboardTile>
  );
}

// Same content as CorrectionComplianceTile, but without the surrounding
// DashboardTile — for embedding inside the unified 30-day-compliance widget.
export function CorrectionComplianceSummary({
  shippedYear,
  skip = false,
  selectedVendor = null,
}: Props) {
  const { tenantId } = useTenant();
  const { setFilters } = useQueryParams<{ omnisearch?: string }>();

  // Same query for both modes — passing vendorSource: null returns
  // all-vendor totals; a real vendor narrows the donut to just that vendor.
  // Suspense-based: a parent Suspense boundary owns the loading fallback,
  // and startTransition in the vendor-select handler keeps the existing
  // donut on screen while new data loads (no jitter on selection change).
  const { data, error } = useSuspenseQuery(
    PROCESSED_MANIFEST_CORRECTION_STATS_QUERY,
    skip || !tenantId
      ? skipToken
      : {
          variables: { tenantId, shippedYear, vendorSource: selectedVendor },
          errorPolicy: "all",
        }
  );

  // Clicking a composition-bar segment filters the manifests grid to that bucket.
  // Only the correctionBucket term is set; tenantId/shippedDate come from the
  // grid's defaultSearch (smart-merged).
  const filterByBucket = (bucket: string) => {
    setFilters((prev) => ({
      ...prev,
      omnisearch: upsertOmnisearchTerm(prev.omnisearch ?? "", "correctionBucket", bucket),
    }));
  };

  return (
    <>
      {renderBody({
        error,
        data,
        shippedYear,
        selectedVendor,
        onBucketClick: filterByBucket,
      })}
    </>
  );
}

function renderBody({
  error,
  data,
  shippedYear,
  selectedVendor,
  onBucketClick,
}: {
  error: unknown;
  data: ProcessedManifestCorrectionStatsQuery | undefined;
  shippedYear: number;
  selectedVendor: string | null;
  onBucketClick: (bucket: string) => void;
}) {
  if (error) {
    return <Typography color="error">Couldn't load 30-day compliance stats</Typography>;
  }

  const stats = data?.processedManifestCorrectionStats;
  if (!stats) {
    return null;
  }

  if (isCorrectionStatsEmpty(stats)) {
    return (
      <Stack spacing={0.5}>
        <Typography variant="h3">—</Typography>
        <Typography variant="body2" color="text.secondary">
          {correctionComplianceEmptyMessage(shippedYear, selectedVendor)}
        </Typography>
      </Stack>
    );
  }

  return (
    <CompositionDonut
      onTime={stats.onTime}
      late={stats.late}
      overdue={stats.overdue}
      inWindow={stats.inWindow}
      onBucketClick={onBucketClick}
    />
  );
}

function CompositionDonut({
  onTime,
  late,
  overdue,
  inWindow,
  onBucketClick,
}: {
  onTime: number;
  late: number;
  overdue: number;
  inWindow: number;
  onBucketClick: (bucket: string) => void;
}) {
  const segments = buildComplianceSegments({ onTime, late, overdue, inWindow });

  if (segments.length === 0) {
    return null;
  }

  // Center label = headline compliance %. Calculated against "concluded"
  // (excludes pending) so a fresh backlog of pending requests doesn't drag
  // the headline number down to 0% — same definition the prior bar used.
  const concluded = onTime + late + overdue;
  const headline = complianceHeadline(onTime, concluded);

  return (
    // Mirrors the Insights facility-mix donut: fixed-size chart on the left,
    // vertically-stacked legend filling the space to its right. `flex: 1` on
    // the legend means the donut+legend group always spans the full column,
    // so the donut never shifts when vendor filters change which legend items
    // render. flexGrow + justifyContent vertically center the donut against
    // the taller monthly-trend chart beside it.
    <Stack sx={{ flexGrow: 1, justifyContent: "center" }}>
      <DonutChart
        // 18rem (288px) comfortably fits the 240px donut diameter
        // (outerRadius 120) — anything smaller clips the arc edges.
        containerHeight="18rem"
        seriesData={segments.map((seg) => ({
          id: seg.key,
          value: seg.value,
          label: `${seg.label} (${seg.value})`,
          color: seg.color,
        }))}
        colors={segments.map((seg) => seg.color)}
        label={headline}
        subLabel="resolved on time"
        legend={{ direction: "column", position: "right" }}
        legendSx={{ minWidth: "150px", flex: 1 }}
        // flex-start, not center: when the column is too narrow for donut +
        // legend, centering splits the overflow to both sides and clips the
        // donut's left edge; pinning left means only the legend truncates.
        justifyContent="flex-start"
        // Animate arc transitions when the vendor filter swaps the data —
        // matches the monthly bar chart instead of blinking to the new state.
        skipAnimation={false}
        onItemClick={(_event, item) => {
          const seg = segments[item.dataIndex];
          if (seg) {
            onBucketClick(seg.bucket);
          }
        }}
      />
    </Stack>
  );
}
